import { useMemo } from 'react';

import { Button } from 'design-system';
import { DownloadIcon, SquareIcon } from 'lucide-react';

import { Permission } from '@/entities/permissions';
import ShareButton from '@/features/_shared/ShareButton';
import UpgradeToPremiumButton from '@/features/_shared/UpgradeToPremium/UpgradeToPremiumButton';
import { usePermission, useUserDateFormat } from '@/features/_shared/user';
import { FACTSHEETS_ENDPOINT } from '@/features/factsheet/constants';
import {
  formatFactsheetDate,
  getVersionHistoryField,
  getFactsheetLink
} from '@/features/factsheet/lib';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import type { FactsheetDetails } from '@/features/factsheet/types';
import { cn } from '@/lib/utils';
import { StorageKeys } from '@/services/storage';
import { useStorageValue } from '@/services/storage/react';
import downloadPDF from '@/shared/lib/download/downloadPDF';

import useItemsTab from '../FactsheetBody/Tabs/ItemsTab/useItemsTab';

// ShareButton/UpgradeToPremiumButton are owned by features/_shared and are not
// migrated to design-system yet — they still expect the old ButtonSize
// ('compact' | 'medium') and `sx` shapes.
const shareBtnSx = { width: '120px' };
// UpgradeToPremiumButton hardcodes minWidth/fontSize in its own base sx, so
// the `compact` Button size preset alone can't shrink it here — override both.
const compactUpgradeBtnSx = {
  minWidth: 'auto',
  height: '1.75rem',
  fontSize: '0.75rem'
};

interface ButtonSectionProps {
  factsheet: FactsheetDetails;
}

const ButtonSection = ({ factsheet }: ButtonSectionProps) => {
  const isCompact = useIsFactsheetCompact();
  const { handleAllItemsSelection, allItemsSelected } = useItemsTab(factsheet);

  const handleDownload = (): void => {
    downloadPDF(`${FACTSHEETS_ENDPOINT}/${factsheet.code}.pdf`, `${factsheet.code}.pdf`);
  };

  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewIndexInformationChart);
  const legacyBtnSize = isCompact ? 'compact' : 'medium';

  return (
    <div
      className={cn(
        'flex flex-shrink-0 flex-wrap items-center gap-3',
        isCompact ? 'py-2' : 'py-3 sm:py-3.5 md:py-4'
      )}
    >
      {!isPermissionGranted && (
        <UpgradeToPremiumButton
          sx={isCompact ? compactUpgradeBtnSx : undefined}
          type='upgrade'
        />
      )}
      <ShareButton
        iconSize={12}
        shareUrl={getFactsheetLink(factsheet.code)}
        size={legacyBtnSize}
        sx={shareBtnSx}
        title={factsheet.title}
      />
      <Button
        variant='secondary'
        disabled={allItemsSelected}
        size={isCompact ? 'sm' : 'default'}
        onClick={handleAllItemsSelection}
      >
        <SquareIcon />
        Add item(s) to selector
      </Button>
      <Button
        variant='secondary'
        size={isCompact ? 'sm' : 'default'}
        onClick={handleDownload}
      >
        <DownloadIcon />
        Download factsheet
      </Button>
    </div>
  );
};

interface DescriptionProps {
  description: string | undefined;
}

const Description = ({ description }: DescriptionProps) => {
  const isCompact = useIsFactsheetCompact();
  if (!description) return null;

  const lineCounts = new Map<string, number>();
  const lines = description.split('\n').map(line => {
    const occurrence = lineCounts.get(line) ?? 0;
    lineCounts.set(line, occurrence + 1);
    return { key: `${line || 'empty-line'}-${occurrence}`, text: line };
  });

  return (
    <div>
      {lines.map(line => (
        <p
          key={line.key}
          className={cn('mb-1', isCompact ? 'text-sm' : 'text-xl')}
        >
          {line.text}
        </p>
      ))}
    </div>
  );
};

interface VersionInfoProps {
  version: string;
  validFrom: string;
  validTo: string;
}

const VersionInfo = ({ version, validFrom, validTo }: VersionInfoProps) => {
  const isCompact = useIsFactsheetCompact();

  return (
    <p className={cn('pt-[2px] italic', isCompact && 'text-xs')}>
      Factsheet version {version}
      {validFrom && `, valid from ${validFrom}`}
      {validTo && ` to ${validTo}`}
    </p>
  );
};

interface FactsheetHeaderProps {
  factsheet: FactsheetDetails;
}

const FactsheetHeader = ({ factsheet }: FactsheetHeaderProps) => {
  const gxToken = useStorageValue(StorageKeys.GxToken);
  const dateFormat = useUserDateFormat();
  const isCompact = useIsFactsheetCompact();

  const validFrom = useMemo(
    () =>
      formatFactsheetDate(
        getVersionHistoryField(factsheet, 'factsheetValidFrom'),
        dateFormat.value
      ),
    [dateFormat.value, factsheet]
  );

  const validTo = useMemo(
    () =>
      formatFactsheetDate(getVersionHistoryField(factsheet, 'factsheetValidTo'), dateFormat.value),
    [dateFormat.value, factsheet]
  );

  return (
    <div className={cn('flex w-full flex-col items-start px-6', isCompact && 'px-4 pt-4')}>
      <div className='flex w-full flex-wrap items-center'>
        <div className='mr-5 min-w-0 max-w-[740px] flex-grow sm:mr-8 md:mr-12'>
          <p className={cn('font-bold', isCompact ? 'text-base' : 'text-2xl')}>{factsheet.title}</p>
        </div>
        {gxToken && <ButtonSection factsheet={factsheet} />}
      </div>
      <Description description={factsheet.description} />
      <VersionInfo
        validFrom={validFrom}
        validTo={validTo}
        version={factsheet.latestFactsheetVersion}
      />
    </div>
  );
};

export default FactsheetHeader;
